import { useEffect } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { Flame } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { usePageTitle } from '../hooks/usePageTitle'
import { LEGACY_PRODUCT_NAME } from '../lib/product'
import { HeroArt } from '../components/HeroArt'
import { HowItWorks } from '../components/HowItWorks'
import { Button, Spinner } from '../components/ui'

/**
 * Steam landing + hero (route map §4 `/steam`). One clear CTA: sign in with
 * Steam, with a calm "how it works" strip below the fold. Redirects
 * Steam-linked users to /steam/groups; golf accounts (no steam_id) stay on
 * the pitch instead of bouncing into screens that need a Steam identity.
 */
export function LandingScreen() {
  usePageTitle(undefined, LEGACY_PRODUCT_NAME)
  const { isAuthenticated, isLoading, signInWithSteam, user } = useAuth()
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as { auth?: string }
  const authError = search?.auth

  useEffect(() => {
    if (!isLoading && isAuthenticated && user?.steam_id) {
      navigate({ to: '/steam/groups' })
    }
  }, [isLoading, isAuthenticated, user, navigate])

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-canvas">
        <Spinner size="lg" />
      </div>
    )
  }

  return (
    <main id="main" tabIndex={-1} className="relative flex min-h-screen flex-col bg-canvas text-text outline-none">
      {/* Hero — the warm circle of light (§8). Sized to invite a scroll to the strip. */}
      <section className="relative flex min-h-[88vh] flex-col">
        <HeroArt />

        <div className="relative z-10 mx-auto flex w-full max-w-3xl flex-1 flex-col items-center justify-center px-4 py-20 text-center sm:px-6">
          {/* Warm scrim behind the text column so the headline always wins (§8.3). */}
          <div
            className="pointer-events-none absolute inset-0 -z-0"
            aria-hidden="true"
            style={{
              background: 'radial-gradient(60% 50% at 50% 45%, oklch(12% 0.01 48 / 0.55), transparent 75%)',
            }}
          />

          {/* Staggered firelight entrance (§4); .fireside-rise is static under prefers-reduced-motion. */}
          <div className="relative">
            <span className="fireside-rise inline-flex items-center gap-2 rounded-chip bg-ember-500/15 px-3 py-1 text-xs font-medium tracking-wide text-ember-300 ring-1 ring-ember-400/30">
              <Flame className="h-3.5 w-3.5" strokeWidth={2} aria-hidden="true" />
              Game night, sorted
            </span>

            <h1
              className="fireside-rise mt-6 font-display text-[clamp(2.5rem,7vw,3.75rem)] leading-[1.05] text-text-strong"
              style={{ animationDelay: '80ms' }}
            >
              What should we play tonight?
            </h1>
            <p
              className="fireside-rise mx-auto mt-5 max-w-prose text-base leading-relaxed text-text-muted"
              style={{ animationDelay: '160ms' }}
            >
              Fireside reads your group&apos;s Steam libraries and finds the handful of games you&apos;ll
              actually all sink hours into — with a clear why for each pick.
            </p>

            {authError && (
              <p className="mt-4 text-sm text-danger-fg" role="alert">
                {authError === 'failed'
                  ? "We couldn't read your Steam sign-in. Try again?"
                  : 'Something went wrong signing in. Try again?'}
              </p>
            )}

            {/* CTA shares one light source with the hero fire (§8.4). */}
            <div className="fireside-rise relative mt-8 inline-block" style={{ animationDelay: '240ms' }}>
              <div
                className="pointer-events-none absolute inset-0 -z-0 blur-2xl"
                aria-hidden="true"
                style={{ background: 'radial-gradient(50% 50% at 50% 50%, oklch(76.9% 0.188 70.08 / 0.20), transparent 70%)' }}
              />
              <Button size="lg" onClick={signInWithSteam} className="relative">
                <Flame className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" />
                Let&apos;s find tonight&apos;s game
              </Button>
            </div>
            {/* Pre-auth trust/consent line — essential copy, so it sits a tier
                above the faint hint register (§7: text-faint is for
                non-essential hints only). */}
            <p className="fireside-rise mt-3 text-sm text-text-muted" style={{ animationDelay: '320ms' }}>
              Sign in with Steam — we only read your library.
            </p>
          </div>
        </div>
      </section>

      {/* How it works — calm register, below the hero firelight (§1.1, §1.7). */}
      <section className="relative z-10 mx-auto w-full max-w-5xl px-4 pb-24 sm:px-6">
        <HowItWorks />
      </section>

      {/* Minimal pre-sign-in footer: the privacy policy is the one link a
          Steam-data product owes visitors before they sign in. Plain anchor —
          /privacy is a Rails page, not a SPA route. Muted, not faint: link
          text is real content and faint sits under 4.5:1 (WCAG 1.4.3). */}
      <footer className="relative z-10 mx-auto w-full max-w-5xl px-4 pb-8 sm:px-6">
        <p className="border-t border-border pt-6 text-center text-xs text-text-muted">
          <a
            href="/privacy"
            className="rounded-control px-1 py-0.5 transition duration-200 ease-out hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ember-400 focus-visible:ring-offset-2 focus-visible:ring-offset-canvas"
          >
            Privacy — how we treat your Steam data
          </a>
        </p>
      </footer>
    </main>
  )
}
